/**
 * Org-aware money formatting for client UI (multi-currency Phase D, step 1).
 *
 * Replaces hardcoded-USD `formatCurrency` (`~/utils/formatters`) on display paths.
 * This is the **org-display** concern only — it is NOT gated by the org FX flag.
 * The currency *symbol* follows `Org.baseCurrencyCode` for every org; foreign
 * transaction currency, dual txn/base display, rate freeze, and GL conversion are
 * separate, FX-flag-gated concerns handled elsewhere.
 *
 * Behavioral contract: `formatMoney(value, "USD", opts)` is a strict parity port of
 * legacy `formatCurrency(value, ...)`, so USD orgs see zero visible change.
 *
 * Boundary: client-only. May import client React context (`~/contexts/orgProvider`);
 * must NOT import Prisma or server-domain modules (see `../ui/README.md`).
 *
 * @see src/modules/multi-currency/ui/README.md § Display contract
 * @see src/utils/formatters.ts (legacy formatCurrency being parity-ported)
 */
import { useCallback } from "react";

import { useOptionalOrg } from "~/contexts/orgProvider";
import { type FormatMoneyOptions, formatMoney } from "~/shared/money/format-money";

export { type FormatMoneyOptions, formatCurrencyOptionLabel, formatCurrencySymbol, formatMoney } from "~/shared/money/format-money";

/** Bound `formatMoney` signature returned by {@link useFormatMoney}. */
export type BoundFormatMoney = (value: string | number | bigint, options?: FormatMoneyOptions) => string;

/**
 * Returns a `formatMoney` bound to the current org's base currency.
 *
 * Uses `useOptionalOrg` so it never throws when rendered outside `OrgProvider`
 * (falls back to USD). For USD orgs this matches legacy `formatCurrency` output.
 *
 * @example
 * const formatMoney = useFormatMoney();
 * formatMoney(order.totalAmount);                 // "$1,234.56"
 * formatMoney(item.amount, { precision: 4 });     // "$12.3456"
 */
export const useFormatMoney = (): BoundFormatMoney => {
  const org = useOptionalOrg();
  const currencyCode = org?.orgBaseCurrencyCode ?? "USD";

  return useCallback(
    (value: string | number | bigint, options?: FormatMoneyOptions) => formatMoney(value, currencyCode, options),
    [currencyCode],
  );
};
